import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ChordChart } from "../components/ChordChart";
import { type Analysis, loadAnalysis, type SongRow } from "../lib/analysis";
import { getSetlist, type SetlistItem } from "../lib/setlists";

/**
 * The printed booklet: every song in the list, in the key the list asks for,
 * one song per page.
 *
 * This is the artefact people actually keep. Two things matter and both are
 * CSS: a song starts on a fresh page, and a chorus never splits across a page
 * break (`break-inside: avoid` on each section, in theme.css) — a chart that
 * turns the page mid-chorus is useless on a stand.
 */
export function Booklet() {
  const { id = "" } = useParams();
  const [rows, setRows] = useState<{ item: SetlistItem; analysis: Analysis }[]>([]);
  const [name, setName] = useState("");

  useEffect(() => {
    getSetlist(id).then(async ({ list, items }) => {
      setName(list?.name ?? "");
      const loaded = await Promise.all(items.map(async (item) => {
        const analysis = item.songs ? await loadAnalysis(item.songs as SongRow) : null;
        return analysis ? { item, analysis } : null;
      }));
      setRows(loaded.filter(Boolean) as { item: SetlistItem; analysis: Analysis }[]);
    });
  }, [id]);

  return (
    <div className="page booklet">
      <div className="no-print" style={{ margin: "20px 0" }}>
        <button className="btn btn-primary" onClick={() => window.print()}>
          Print or save as PDF
        </button>
        <span className="muted small" style={{ marginLeft: 12 }}>
          {rows.length} songs, one per page
        </span>
      </div>

      <h1 className="serif" style={{ fontSize: 30 }}>{name}</h1>

      {rows.map(({ item, analysis }) => (
        <article key={item.position} className="booklet-song">
          <header className="song-head" style={{ marginTop: 0 }}>
            <div>
              <h2 className="serif song-title" style={{ fontSize: 28 }}>{analysis.title}</h2>
              <p className="serif song-artist" style={{ fontSize: 16 }}>{analysis.artist}</p>
            </div>
            <div className="song-meta mono">
              {analysis.analysis.key} · {Math.round(analysis.analysis.bpm)} bpm
              {item.transpose ? ` · ${item.transpose > 0 ? "+" : ""}${item.transpose}` : ""}
              {item.capo ? ` · capo ${item.capo}` : ""}
            </div>
          </header>
          {item.note && <p className="muted small">{item.note}</p>}
          <ChordChart
            analysis={analysis}
            view={{ transpose: item.transpose, capo: item.capo ?? 0, simple: true }}
            currentTime={-1}
          />
        </article>
      ))}
    </div>
  );
}
